import React, { useState, useRef } from "react";
import styled from "styled-components";

import { isFullsceenAllowed, toggleFullscreen } from "../helpers/fullscreen";
import { stopPropagation } from "../helpers/stopPropagation";

import {
    ArrowDropDown as ArrowDropDownIcon,
    Close as CloseIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
    ViewModule as ViewModuleIcon,
    Fullscreen as FullscreenIcon,
    ChromeReaderMode as ChromeReaderModeIcon,
    ModeComment as ModeCommentIcon
} from "@material-ui/icons";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap20 } from "js/react/components/Gap";

import PresentationActionsMenu from "./PresentationActionsMenu";

const BottomBarContainer = styled.div`
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  overflow: hidden;
  z-index: 75;
  cursor: default;
`;

const BottomBar = styled.div.attrs(({ visible }) => ({
    style: {
        opacity: visible ? 100 : 0
    }
}))`
  height: 44px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  padding: 6px 10px 5px 10px;
  transition: opacity ease-in-out 0.5s;
  cursor: default;
`;

const SlideIndex = styled.div`
  user-select: none;
  text-transform: uppercase;
  font-size: 13px;
  color: #fff;
  margin-left: 8px;
  margin-right: 8px;
`;

const ButtonWithLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  color: #e1e1e1;
  cursor: pointer;

  label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
    margin-left: 10px;
  }

  &:hover {
    color: #11a9e2;
  }

  @media (max-width: 950px) {
    label {
      display: none;
    }

    margin-right: 15px;
  }
`;

const Spacer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-basis: 0;
  justify-content: flex-end;
`;

function getStyledIconButton(Icon, style = {}) {
    return styled(Icon).attrs(() => ({ style }))`
      color: #ccc;
      cursor: pointer;

      &:hover {
        color: #11a9e2;
      }
    `;
}

const PrevSlideButton = getStyledIconButton(NavigateBeforeIcon);
const NextSlideButton = getStyledIconButton(NavigateNextIcon);
const ThumbnailsButton = getStyledIconButton(ViewModuleIcon, { marginRight: "10px" });

function PlayerControls({
    visible,
    isLoggedIn,
    onExit,
    showThumbnailsPane,
    advanceToSlide,
    currentSlideIndex,
    isPresenterAvailable,
    togglePresenter,
    hasViewerComments,
    toggleComments,
    isPlayerSettingsPreview,
    onDownloadPdf,
    onCopyDeck,
    allowCopyLink,
    onEditPresentation,
    onSwitchPresentation,
    actionsMenuIsOpen,
    onToggleActionsMenu,
    analytics,
    username,
    slidesCount = 1,
}) {
    const moreButtonRef = useRef();

    const showActionsMenu = onDownloadPdf || onCopyDeck || allowCopyLink || onEditPresentation || onSwitchPresentation;

    const allowFullscreenOption = isFullsceenAllowed();

    return (
        <BottomBarContainer>
            <BottomBar
                visible={visible}
                onClick={event => stopPropagation(event)}
            >
                <Spacer />

                <FlexBox middle style={{ marginLeft: 30 }}>
                    <PrevSlideButton onClick={() => advanceToSlide(-1)} />
                    <SlideIndex>{currentSlideIndex + 1} / {slidesCount}</SlideIndex>
                    <NextSlideButton onClick={() => advanceToSlide(1)} />
                    <Gap20 />
                    <ThumbnailsButton onClick={showThumbnailsPane} />
                </FlexBox>

                <Spacer>
                    {allowFullscreenOption &&
                        <ButtonWithLabel onClick={!isPlayerSettingsPreview ? toggleFullscreen : null}>
                            <FullscreenIcon style={{ fontSize: 26 }} />
                            <label>FullScreen</label>
                        </ButtonWithLabel>
                    }
                    {isPresenterAvailable &&
                        <ButtonWithLabel onClick={togglePresenter}>
                            <ChromeReaderModeIcon style={{ fontSize: 22, marginBottom: 2 }} />
                            <label>Presenter</label>
                        </ButtonWithLabel>
                    }
                    {hasViewerComments &&
                        <ButtonWithLabel onClick={() => toggleComments()}>
                            <ModeCommentIcon style={{ fontSize: 22, marginTop: 2 }} />
                            <label>Comments</label>
                        </ButtonWithLabel>
                    }

                    <Gap20 />

                    {showActionsMenu &&
                        <ButtonWithLabel style={{ marginRight: 0 }}
                            ref={moreButtonRef}
                            onClick={() => onToggleActionsMenu(true)}
                        >
                            <label>More Actions</label>
                            <ArrowDropDownIcon />
                        </ButtonWithLabel>
                    }
                    {showActionsMenu &&
                        <PresentationActionsMenu
                            isLoggedIn={isLoggedIn}
                            onCopyDeck={onCopyDeck}
                            onDownloadPdf={onDownloadPdf}
                            allowCopyLink={allowCopyLink}
                            onEditPresentation={onEditPresentation}
                            onSwitchPresentation={onSwitchPresentation}
                            isOpen={actionsMenuIsOpen}
                            onClose={() => onToggleActionsMenu(false)}
                            anchorEl={moreButtonRef.current}
                            analytics={analytics}
                            username={username}
                        />
                    }

                    {onExit &&
                        <ButtonWithLabel onClick={onExit}>
                            <CloseIcon />
                            <label>Exit</label>
                        </ButtonWithLabel>}
                </Spacer>
            </BottomBar>
        </BottomBarContainer>
    );
}

export default PlayerControls;
